// Setup Guide personalization helpers.
//
// Principle: "show if user said this OR if the data already exists for this."
// Stated intent at signup gates initial visibility; existing data overrides it.
// Otherwise edit-existing paths break for users whose state has drifted from
// their original signup answers (e.g. a 'Just me' business that has since
// hired 3 employees).
//
// These helpers normalize the messy reality of onboarding_answers:
//   - teamSize has two conventions ('Just me' titlecase from the older signup,
//     'just_me' snake_case from the newer one)
//   - peopleTypes uses plural forms ('contractors', 'operators')
//   - cluster_id on businesses is garbage data — clusters are documentation
//     only. Use categoryId + industryTaxonomy features instead.

import { getCategoryFeatures } from '../../../data/industryTaxonomy';

export type NormalizedTeamSize = 'solo' | 'small' | 'medium' | 'large' | 'unknown';

export function normalizeTeamSize(raw: unknown): NormalizedTeamSize {
  if (typeof raw !== 'string') return 'unknown';
  const lower = raw.toLowerCase().trim();
  if (lower === 'just me' || lower === 'just_me' || lower === 'solo') return 'solo';
  if (lower === 'small' || lower.includes('2-5')) return 'small';
  if (lower === 'medium' || lower.includes('6-15')) return 'medium';
  if (lower === 'large') return 'large';
  return 'unknown';
}

export function hasContractorIntent(peopleTypes: unknown): boolean {
  if (!Array.isArray(peopleTypes)) return false;
  return peopleTypes.some(
    t =>
      typeof t === 'string' &&
      (t.toLowerCase() === 'contractor' || t.toLowerCase() === 'contractors'),
  );
}

export function hasOperatorIntent(peopleTypes: unknown): boolean {
  if (!Array.isArray(peopleTypes)) return false;
  return peopleTypes.some(
    t =>
      typeof t === 'string' &&
      (t.toLowerCase() === 'operator' || t.toLowerCase() === 'operators'),
  );
}

export function isBookingOriented(categoryId: string | null | undefined): boolean {
  if (!categoryId) return false;
  return getCategoryFeatures(categoryId).showBookings === true;
}

// Item 10 (commission tracking) is only offered for categories where staff
// commonly earn commission — beauty chairs, wellness practitioners, auto
// service advisors. Clusters are doc-only so check categoryId directly.
export function isCommissionEligibleCategory(
  categoryId: string | null | undefined,
): boolean {
  if (!categoryId) return false;
  return ['beauty', 'wellness', 'automotive'].includes(categoryId);
}
